import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TYPE, UI_MUTED, UI_SECTION } from '@/lib/typography';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { cn } from '@/utils/cn';
import {
  EMPTY_INVOICE_FILTERS,
  INVOICE_DATE_FIELDS,
  INVOICE_DATE_PRESETS,
  INVOICE_FILTER_STATUSES,
  countActiveInvoiceFilterGroups,
  formatFilterAmountInput,
  parseFilterAmountToCents,
  type InvoiceDateField,
  type InvoiceDatePreset,
  type InvoiceFilterStatus,
  type InvoicePageFilters,
} from './invoiceFilters';

interface InvoiceFiltersPopoverProps {
  clinicId: string | null;
  filters: InvoicePageFilters;
  patientLabels: Record<string, string>;
  paymentMethods: string[];
  onApply: (filters: InvoicePageFilters) => void;
}

function withoutSearch(filters: InvoicePageFilters): InvoicePageFilters {
  return { ...filters, search: '' };
}

export function InvoiceFiltersPopover({
  clinicId,
  filters,
  patientLabels,
  paymentMethods,
  onApply,
}: InvoiceFiltersPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<InvoicePageFilters>(withoutSearch(filters));
  const [patientQuery, setPatientQuery] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const picker = usePatientPicker(clinicId, patientQuery);
  const activeCount = countActiveInvoiceFilterGroups(filters);

  useEffect(() => {
    if (!open) return;
    setDraft(withoutSearch(filters));
    setAmountMin(
      filters.amountMinCents != null ? formatFilterAmountInput(filters.amountMinCents) : '',
    );
    setAmountMax(
      filters.amountMaxCents != null ? formatFilterAmountInput(filters.amountMaxCents) : '',
    );
    setPatientQuery('');
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const selectedPatients = useMemo(() => {
    return draft.patientIds.map((id) => ({
      id,
      label: patientLabels[id] ?? id,
    }));
  }, [draft.patientIds, patientLabels]);

  const toggleStatus = (status: InvoiceFilterStatus) => {
    setDraft((prev) => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter((item) => item !== status)
        : [...prev.statuses, status],
    }));
  };

  const addPatient = (patient: PatientPickerItem) => {
    setDraft((prev) =>
      prev.patientIds.includes(patient.id)
        ? prev
        : { ...prev, patientIds: [...prev.patientIds, patient.id] },
    );
    setPatientQuery('');
  };

  const removePatient = (id: string) => {
    setDraft((prev) => ({
      ...prev,
      patientIds: prev.patientIds.filter((item) => item !== id),
    }));
  };

  const toggleMethod = (method: string) => {
    setDraft((prev) => ({
      ...prev,
      paymentMethods: prev.paymentMethods.includes(method)
        ? prev.paymentMethods.filter((item) => item !== method)
        : [...prev.paymentMethods, method],
    }));
  };

  const applyDraft = () => {
    onApply({
      ...draft,
      search: filters.search,
      amountMinCents: parseFilterAmountToCents(amountMin),
      amountMaxCents: parseFilterAmountToCents(amountMax),
    });
    setOpen(false);
  };

  const clearDraft = () => {
    setDraft(withoutSearch(EMPTY_INVOICE_FILTERS));
    setAmountMin('');
    setAmountMax('');
  };

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="secondary"
        data-testid="invoice-filters-button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
        className={cn(activeCount > 0 && 'border-ds-teal bg-ds-teal-tint text-ds-teal')}
      >
        <Filter />
        {activeCount > 0
          ? t('invoices.filters_button_count', { count: activeCount })
          : t('invoices.filters_button')}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={t('invoices.filters_title')}
          data-testid="invoice-filters-panel"
          className="absolute left-0 top-full z-30 mt-s2 w-[22.5rem] max-w-[calc(100vw-2rem)] rounded-pop border border-ds-line bg-ds-card p-s4 shadow-pop"
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className={UI_SECTION}>{t('invoices.filters_title')}</h2>
            <Button
              type="button"
              variant="tertiary"
              size="icon"
              onClick={() => setOpen(false)}
              aria-label={t('common.close')}
            >
              <X />
            </Button>
          </div>

          <div className="space-y-4">
            <fieldset>
              <legend className={`mb-1.5 ${UI_MUTED}`}>
                {t('invoices.filters_status')}
              </legend>
              <div className="grid grid-cols-2 gap-1.5">
                {INVOICE_FILTER_STATUSES.map((status) => (
                  <Checkbox
                    key={status}
                    checked={draft.statuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                    label={t(`invoices.status_${status}`)}
                    className="min-h-8"
                  />
                ))}
              </div>
            </fieldset>

            <div>
              <label className={`mb-1.5 block ${UI_MUTED}`}>
                {t('invoices.filters_patient')}
              </label>
              {selectedPatients.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {selectedPatients.map((patient) => (
                    <span
                      key={patient.id}
                      className={`inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-s2 py-0.5 ${TYPE.body} font-medium text-ds-teal`}
                    >
                      {patient.label}
                      <button
                        type="button"
                        onClick={() => removePatient(patient.id)}
                        aria-label={t('invoices.filters_remove')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Input
                type="search"
                value={patientQuery}
                onChange={(event) => setPatientQuery(event.target.value)}
                placeholder={t('invoices.filters_patient_search')}
              />
              {patientQuery.trim() && (picker.data?.length ?? 0) > 0 && (
                <div className="mt-s1 max-h-40 overflow-y-auto rounded-pop border border-ds-line bg-ds-card shadow-pop">
                  {picker.data?.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => addPatient(patient)}
                      className={`block w-full px-s3 py-s2 text-left ${TYPE.body} text-ds-text hover:bg-ds-sunk`}
                    >
                      <span className="block">
                        {patient.first_name} {patient.last_name}
                      </span>
                      {formatPatientTelephoneNumbers(
                        patient.telephone_subscribers,
                        patient.phone,
                      ) ? (
                        <span className="block text-ds-muted">
                          {formatPatientTelephoneNumbers(
                            patient.telephone_subscribers,
                            patient.phone,
                          )}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className={`mb-1.5 block ${UI_MUTED}`}>
                {t('invoices.filters_date')}
              </label>
              <div className="space-y-2">
                <Select
                  value={draft.dateField}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      dateField: event.target.value as InvoiceDateField,
                    }))
                  }
                  aria-label={t('invoices.filters_date_field')}
                >
                  {INVOICE_DATE_FIELDS.map((field) => (
                    <option key={field} value={field}>
                      {t(`invoices.filters_date_${field}`)}
                    </option>
                  ))}
                </Select>
                <Select
                  value={draft.datePreset ?? ''}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      datePreset: (event.target.value || null) as InvoiceDatePreset | null,
                    }))
                  }
                  aria-label={t('invoices.filters_date_preset')}
                >
                  <option value="">{t('invoices.filters_date_none')}</option>
                  {INVOICE_DATE_PRESETS.map((preset) => (
                    <option key={preset} value={preset}>
                      {t(`invoices.filters_date_${preset}`)}
                    </option>
                  ))}
                </Select>
                {draft.datePreset === 'custom' && (
                  <div className="grid grid-cols-2 gap-s2">
                    <Input
                      type="date"
                      label={t('invoices.filters_date_from')}
                      value={draft.customFrom ?? ''}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          customFrom: event.target.value || null,
                        }))
                      }
                    />
                    <Input
                      type="date"
                      label={t('invoices.filters_date_to')}
                      value={draft.customTo ?? ''}
                      onChange={(event) =>
                        setDraft((prev) => ({
                          ...prev,
                          customTo: event.target.value || null,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className={`mb-1.5 block ${UI_MUTED}`}>
                {t('invoices.filters_amount')}
              </label>
              <div className="grid grid-cols-2 gap-s2">
                <Input
                  type="text"
                  inputMode="decimal"
                  lang="da"
                  value={amountMin}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '' || /^\d*[.,]?\d{0,2}$/.test(raw)) setAmountMin(raw);
                  }}
                  placeholder={t('invoices.filters_amount_min')}
                />
                <Input
                  type="text"
                  inputMode="decimal"
                  lang="da"
                  value={amountMax}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === '' || /^\d*[.,]?\d{0,2}$/.test(raw)) setAmountMax(raw);
                  }}
                  placeholder={t('invoices.filters_amount_max')}
                />
              </div>
            </div>

            {paymentMethods.length > 0 && (
              <fieldset>
                <legend className={`mb-1.5 ${UI_MUTED}`}>
                  {t('invoices.filters_payment_method')}
                </legend>
                <div className="max-h-28 space-y-1 overflow-y-auto">
                  {paymentMethods.map((method) => (
                    <Checkbox
                      key={method}
                      checked={draft.paymentMethods.includes(method)}
                      onChange={() => toggleMethod(method)}
                      label={t(`invoices.payment_method_${method}`, method)}
                      className="min-h-8"
                    />
                  ))}
                </div>
              </fieldset>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <Button type="button" variant="tertiary" size="sm" onClick={clearDraft}>
              {t('invoices.filters_clear_all')}
            </Button>
            <Button size="sm" onClick={applyDraft}>
              {t('invoices.filters_apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
